import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { FileText, Download, Loader2, ReceiptText, ChevronLeft } from 'lucide-react'
import { pdf } from '@react-pdf/renderer'
import { supabase } from '@/infrastructure/supabase/client'
import { useAuthStore } from '@/store/authStore'
import type { InvoiceData, BillingProfile } from '@/components/billing/InvoicePDF'

const SUPABASE_URL = import.meta.env['VITE_SUPABASE_URL'] as string

interface StripeInvoice extends InvoiceData {
  number?: string | null
  status?: string | null
  hosted_url?: string | null
  pdf_url?: string | null
}

function fmt(amount: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100)
}

export function InvoicesPage() {
  const { tenant } = useAuthStore()
  const [invoices, setInvoices] = useState<StripeInvoice[]>([])
  const [profile, setProfile] = useState<BillingProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState<string | null>(null)

  useEffect(() => {
    if (!tenant?.id) return
    const fetchAll = async () => {
      const { data: { session } } = await supabase.auth.getSession()

      const [invoiceRes, profileRes] = await Promise.all([
        fetch(`${SUPABASE_URL}/functions/v1/stripe-invoices`, {
          headers: { Authorization: `Bearer ${session?.access_token}` },
        }).then((r) => r.json() as Promise<{ invoices: StripeInvoice[] }>),

        supabase
          .from('billing_profiles')
          .select('*')
          .eq('tenant_id', tenant.id)
          .maybeSingle()
          .then(({ data }) => data as BillingProfile | null),
      ])

      setInvoices(invoiceRes.invoices ?? [])
      setProfile(profileRes)
      setLoading(false)
    }
    void fetchAll()
  }, [tenant?.id])

  const hasProfile = !!(profile?.company_name || profile?.tax_id)

  const handleDownload = async (invoice: StripeInvoice, docType: 'receipt' | 'invoice') => {
    const key = `${invoice.id}-${docType}`
    setDownloading(key)
    try {
      const { InvoicePDF: PDFDoc } = await import('@/components/billing/InvoicePDF')
      const blob = await pdf(<PDFDoc invoice={invoice} profile={profile} docType={docType} />).toBlob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${docType}-${invoice.number ?? invoice.id}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setDownloading(null)
    }
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-10 px-6">
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-12 rounded-xl bg-gray-100" />)}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto py-10 px-6">
      <div className="mb-2">
        <Link
          to="/billing"
          className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-violet-600 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Billing
        </Link>
      </div>

      <div className="mb-8 flex items-center gap-3">
        <ReceiptText className="h-6 w-6 text-violet-600" />
        <h1 className="text-2xl font-bold text-gray-900">Payment history</h1>
        {invoices.length > 0 && (
          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-sm text-gray-500">{invoices.length}</span>
        )}
      </div>

      {!hasProfile && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Add your <Link to="/billing" className="underline font-medium">billing details</Link> to enable invoice downloads.
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white py-16 text-center">
          <FileText className="h-12 w-12 text-gray-200 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-500">No payments yet.</p>
          <p className="text-xs text-gray-300 mt-1">They'll appear here once you complete a subscription.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wide">Date</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wide">No.</th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wide">Description</th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wide">Amount</th>
                  <th className="py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wide text-center">Download</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                    <td className="py-3.5 px-4 text-gray-600 whitespace-nowrap">
                      {new Date(inv.created * 1000).toLocaleDateString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric',
                      })}
                    </td>
                    <td className="py-3.5 px-4 text-gray-400 font-mono text-xs whitespace-nowrap">
                      {inv.number ?? inv.id.slice(-8)}
                    </td>
                    <td className="py-3.5 px-4 text-gray-700 max-w-[200px] truncate">
                      {inv.lines[0]?.description ?? 'Brix subscription'}
                    </td>
                    <td className="py-3.5 px-4 text-right font-semibold text-gray-900 whitespace-nowrap">
                      {fmt(inv.amount, inv.currency)}
                    </td>
                    <td className="py-3.5 px-4">
                      <div className="flex items-center justify-center gap-2">
                        {/* Receipt — always visible */}
                        <button
                          onClick={() => { void handleDownload(inv, 'receipt') }}
                          disabled={downloading === `${inv.id}-receipt`}
                          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-violet-600 hover:bg-violet-50 border border-violet-200 transition-colors disabled:opacity-50"
                        >
                          {downloading === `${inv.id}-receipt` ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Download className="h-3 w-3" />
                          )}
                          Receipt
                        </button>

                        {/* Invoice — only if billing profile has data */}
                        {hasProfile && (
                          <button
                            onClick={() => { void handleDownload(inv, 'invoice') }}
                            disabled={downloading === `${inv.id}-invoice`}
                            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 border border-gray-200 transition-colors disabled:opacity-50"
                          >
                            {downloading === `${inv.id}-invoice` ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Download className="h-3 w-3" />
                            )}
                            Invoice
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
